import { Flex, GridAlpha, Heading, useToasts } from '@applyboard/crystal-ui'
import styled from '@emotion/styled'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ApplicationForms,
  ApplicationTimeline,
  BackToApplication
} from '../../components/Application'
import { DesktopHeader } from '../../components/Application/ApplicationHeader/DesktopHeader'
import ApplicationStateTag from '../../components/Application/ApplicationStateTag/ApplicationStateTag'
import { StudentApplication } from '../../components/Application/types'
import { Loading } from '../../components/Loading'
import { NavBars } from '../../components/NavBars'
import {
  ApplicationState,
  GetProgramIntakeResponse,
  RawApplicationAggregationResponse,
  RawApplicationResponse,
  useGetApplication,
  useGetProgramIntake,
  useGetStudent,
  useSubmitApplication,
  useUpdateApplication,
  usePolicyEvaluation,
} from '../../hooks'
import { useGetTimelineItems } from '../../hooks/useGetTimelineItems'
import { ApplicationSteps, GenericError, hasApplicationFee } from '../../utils'
import { getStateCode } from '../../utils/applicationStateHelpers'
import { DropdownMenu } from './DropdownMenu'
import { IntakeClosedDialog } from './IntakeClosedDialog'
import { SubmitApprovalDialog } from './SubmitApprovalDialog'
import { PayAndSubmitApprovalDialog, SubmitConfirmation } from './SubmitConfirmation'
import { RawApplicationPagePropsResponse, useGetApplicationPageProps } from '../../hooks/useGetApplicationPageProps'
import { SpecificFieldValidationErrorField, validateRequiredSpecificFields } from '../../utils/specificFieldValidations'
import { SpecificFieldsValidationErrorsDialog } from './SpecificFieldsValidationErrorsDialog'
import { SelectProgram } from './SelectProgram'
import { SelectAlternateProgram } from './SelectAlternateProgram'
import { PolicyBanner } from '../../components/PolicyBanner'
import { PolicyBlockedDialog } from './PolicyBlockedDialog'

// These props is only meant to be used in tests, so that we can force the test to render the application form by specifying the current step
interface CreateApplicationPageProps {
  currentStepOverride?: number
  temporaryApplicationOverride?: StudentApplication
}

export function CreateApplicationPage({
  currentStepOverride,
  temporaryApplicationOverride,
}: CreateApplicationPageProps) {
  const toast = useToasts()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useLocation()
  const currentApplicationId = searchParams.get('applicationId') || ''
  const paymentStatus = searchParams.get('status') || ''

  const { isLoadingApplication, application, intakePolicyEvaluation } = useGetApplication({
    id: currentApplicationId,
  })
  const { isLoadingApplicationPageProps } = useGetApplicationPageProps()
  const { student } = useGetStudent({ id: application?.relationships?.student?.data.id })

  const [temporaryApplication, setTemporaryApplication] = useState<StudentApplication | null>()
  const { isUpdatingApplication, updateApplication } = useUpdateApplication({
    id: currentApplicationId,
  })
  const { lastTimeline, timelineItems } = useGetTimelineItems(application)

  const viewApplicationPageRef = useRef<HTMLDivElement>(null)

  const { isSubmittingApplication, submitApplication } = useSubmitApplication({
    id: currentApplicationId,
  })

  const [showSpecificFieldsValidationErrorsDialog, setShowSpecificFieldsValidationErrorsDialog] =
    useState(false)
  const [specificFieldsValidationErrors, setSpecificFieldsValidationErrors] = useState<
    SpecificFieldValidationErrorField[]
  >([])

  const [showSubmitApprovalDialog, setShowSubmitApprovalDialog] = useState(false)
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false)
  const [showPolicyBlockedDialog, setShowPolicyBlockedDialog] = useState(false)

  const [currentStep, setCurrentStep] = useState(ApplicationSteps.PROGRAM_SELECT)
  let selectedTab = currentStep - 1

  const isEditingProgramRef = useRef(false)

  const { policyEvaluation, canSubmitApplication } =
    usePolicyEvaluation(intakePolicyEvaluation)

  useEffect(() => {
    if (currentStepOverride) {
      setCurrentStep(currentStepOverride)
    }
    if (temporaryApplicationOverride) {
      setTemporaryApplication(temporaryApplicationOverride as StudentApplication)
    }
  }, [currentStepOverride, temporaryApplicationOverride])

  useEffect(() => {
    if (application && (currentStep === ApplicationSteps.PROGRAM_SELECT || currentStep === ApplicationSteps.ALTERNATE_PROGRAM_SELECT) && !isEditingProgramRef.current) {
      setCurrentStep(ApplicationSteps.PERSONAL_INFORMATION)
    }
    if (isEditingProgramRef.current) {
      isEditingProgramRef.current = false
    }
  }, [application, currentStep])

  useEffect(() => {
    if (
      getStateCode(application?.attributes?.applicationState) === ApplicationState.DRAFT &&
      (paymentStatus === 'error' || paymentStatus === 'cancelled')
    ) {
      toast.negative(
        new Error(`Your payment could not be processed for application submission. Please make sure you
           have sufficient funds and your payment details are correct or try an alternative
           payment method.`),
      )
    } else if (paymentStatus === 'success') {
      navigate(`/`, { replace: true })
    }
  }, [paymentStatus, application?.attributes?.applicationState])

  const { intake, isLoadingIntake } = useGetProgramIntake({
    id: application?.attributes?.programSelected?.programIntakeId,
  })

  const isIntakeClosed = useMemo(() => {
    if (isLoadingIntake || !intake) return false
    return intake?.attributes?.isAvailable === false
  }, [intake])

  const [openIntakeClosedDialog, setOpenIntakeClosedDialog] = useState(isIntakeClosed)

  useEffect(() => {
    setOpenIntakeClosedDialog(isIntakeClosed)
  }, [isIntakeClosed])

  const queryParam = searchParams.get('pageNumber')
  const applicationId = searchParams.get('applicationId')

  useEffect(() => {
    if (location.pathname === '/applications/new' && !!queryParam && !applicationId && currentStep !== ApplicationSteps.ALTERNATE_PROGRAM_SELECT) {
      setCurrentStep(ApplicationSteps.PROGRAM_SELECT)
      setTemporaryApplication(null)
    }
  }, [location, queryParam, applicationId, application, setSearchParams])

  useEffect(() => {
    if (application && currentStep < 7) {
      if (getStateCode(application.attributes?.applicationState) !== ApplicationState.DRAFT) {
        navigate(`/applications/${application.id}`, { replace: true })
        toast.warning(
          `The student's application was previously updated. The page has been refreshed to reflect the current status.`,
        )
      } else {
        navigate(`/applications/new?applicationId=${application.id}`, { replace: true })
      }
    }

    if (currentStep > ApplicationSteps.PROGRAM_SELECT) {
      window.scrollTo(0, 0)
    }
  }, [application, navigate, currentStep, toast])

  const paySubmitButtonRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (showSubmitApprovalDialog === false && paySubmitButtonRef.current) {
      paySubmitButtonRef.current.focus()
    }
  }, [showSubmitApprovalDialog])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isLoadingApplication && viewApplicationPageRef.current) {
        viewApplicationPageRef.current.focus()
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [isLoadingApplication])

  const handleUpdateApplication = (intakeId: string | null, alternateProgramId?: string[]) => {
    if (!intakeId && !alternateProgramId) {
      toast.negative(new Error('Failed to update application.'))
      return
    }
    updateApplication(
      {
        attributes: {
          ...(intakeId ? {
            programSelected: {
              priority: 0,
              programIntakeId: intakeId,
            },
          } : {}),
          ...(alternateProgramId ? {
            alternateProgramSelected: alternateProgramId.map(id => ({
              programIntakeId: id,
            })),
          } : {})
        },
      },
      {
        onSuccess: response => {
          queryClient.invalidateQueries({ queryKey: ['intake-search'] })
          queryClient.invalidateQueries({ queryKey: ['applications', response.data.id] })
          setSearchParams({ applicationId: response.data.id })
          setCurrentStep(step => step + 1)

          toast.positive(
            `The student's application has been successfully updated! Please review the requirements to confirm that all required information has been submitted.`,
          )
        },
        onError: err => {
          if (err instanceof Error) {
            toast.negative(new Error(err.message))
            isEditingProgramRef.current = false
          }
        },
      },
    )
  }

  const onCreatedApplication = (applicationId: string) => {
    setSearchParams({ applicationId })
    setCurrentStep(ApplicationSteps.PERSONAL_INFORMATION)
  }

  const handleSubmitApplication = (
    successCallback: (response: RawApplicationResponse) => void,
    errorCallback: (err: GenericError) => void,
  ) => {
    submitApplication(undefined, {
      onSuccess: response => {
        successCallback(response)
      },
      onError: err => {
        if (err instanceof Error) {
          errorCallback(err)
        }
      },
    })
  }

  const handleSubmitConfirmation = async () => {
    handleSubmitApplication(
      (response: RawApplicationResponse) => {
        queryClient.setQueryData(
          ['applications', response.data.id],
          (oldData: RawApplicationAggregationResponse): RawApplicationAggregationResponse => {
            return {
              data: {
                ...oldData.data,
                attributes: {
                  ...oldData.data.attributes,
                  application: response.data,
                },
              },
            }
          },
        )

        setShowSubmitApprovalDialog(false)
        setShowSubmitConfirmation(true)
      },
      (err: GenericError) => {
        setShowSubmitApprovalDialog(false)
        setShowSubmitConfirmation(false)
        toast.negative(err)
      },
    )
  }

  const handleSubmitApplicationTrigger = async () => {
    const formId = timelineItems[timelineItems.length - 1]?.formId
    if (formId) {
      try {
        const formElement = document.getElementById(formId) as HTMLFormElement
        if (formElement) {
          formElement.requestSubmit()

          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['program-intake', intake?.id] }),
            queryClient.invalidateQueries({ queryKey: ['applications', application?.id] }),
            queryClient.invalidateQueries({ queryKey: ['application-page-props'] }),
          ])

          const [updatedAppData, updatedIntake, updatedApplicationPageProps] = [
            queryClient.getQueryData([
              'applications',
              application?.id,
            ]) as RawApplicationAggregationResponse,
            queryClient.getQueryData([
              'program-intake',
              intake?.id,
            ]) as GetProgramIntakeResponse,
            queryClient.getQueryData(['application-page-props']) as RawApplicationPagePropsResponse,
          ]

          if (
            getStateCode(updatedAppData.data.attributes.application.attributes?.applicationState) !==
            ApplicationState.DRAFT
          ) {
            toast.negative(
              new Error(
                'Payment in progress: Another payment is already being processed for this application',
              ),
            )
            navigate(`/applications/${updatedAppData.data.id}`)
            return
          }

          const { isRequiredSpecificFieldsValid, requiredSpecificFieldsErrors } =
            validateRequiredSpecificFields(
              updatedApplicationPageProps?.data?.specificFields,
              student,
              updatedAppData?.data?.attributes?.application,
            )

          if (updatedIntake?.data?.attributes?.isAvailable === false) {
            setOpenIntakeClosedDialog(true)
          } else if (!canSubmitApplication) {
            setShowPolicyBlockedDialog(true)
          } else if (isRequiredSpecificFieldsValid) {
            setShowSubmitApprovalDialog(true)
          } else {
            setSpecificFieldsValidationErrors(requiredSpecificFieldsErrors)
            setShowSpecificFieldsValidationErrorsDialog(true)
          }
        } else {
          throw new Error('Failed to submit application. Please try again.')
        }
      } catch (error) {
        toast.negative(error as GenericError)
      }
    }
  }

  const onPreviousTabButton = () => {
    queryClient.invalidateQueries({
      queryKey: ['applications', application?.id],
    })
    setCurrentStep(selectedTab)
    window.scroll({
      top: 0,
    })
  }

  if ((currentApplicationId && isLoadingApplication) || isLoadingApplicationPageProps) {
    return (
      <NavBars>
        <Loading />
      </NavBars>
    )
  }

  if ((!application && !temporaryApplication) || currentStep === ApplicationSteps.PROGRAM_SELECT || currentStep === ApplicationSteps.ALTERNATE_PROGRAM_SELECT) {
    if (currentStep === ApplicationSteps.ALTERNATE_PROGRAM_SELECT) {
      return (
        <SelectAlternateProgram
          application={application}
          isUpdatingApplication={isUpdatingApplication}
          temporaryApplication={temporaryApplication}
          setTemporaryApplication={setTemporaryApplication}
          setCurrentStep={setCurrentStep}
          handleUpdateApplication={handleUpdateApplication}
          currentApplicationId={currentApplicationId}
        />
      )
    }

    return (
      <SelectProgram
        application={application}
        isUpdatingApplication={isUpdatingApplication}
        setTemporaryApplication={setTemporaryApplication}
        setCurrentStep={setCurrentStep}
        handleUpdateApplication={handleUpdateApplication}
        currentApplicationId={currentApplicationId}
      />
    )
  }

  if (currentStep < ApplicationSteps.PERSONAL_INFORMATION + 1) {
    selectedTab = timelineItems.find(item => !item.completed)?.step ?? ApplicationSteps.PERSONAL_INFORMATION //return the first incomplete tab
  }

  return (
    <ApplicationWrapper tabIndex={-1} ref={viewApplicationPageRef}>
      <NavBars largePage>
        <BackToApplication />
        {policyEvaluation && !canSubmitApplication ? (
          <PolicyBanner
            evaluation={policyEvaluation}
            studentName={student?.attributes?.personalInformation?.previousName ?? student?.attributes?.personalInformation?.preferredName ?? undefined}
            intakeTerm={application?.attributes?.programSelected?.programIntakeTerm?.name}
          />
        ) : null}
        <Flex direction="column" gap={10}>
          <Flex wrap={false} align="center" justify="between" gap={4}>
            <Flex
              hideBelow={selectedTab > lastTimeline && showSubmitApprovalDialog ? 'sm' : undefined}
              align={{ xs: 'start', md: 'center' }}
              gap={4}
              direction={{ xs: 'column', md: 'row' }}
              py={4}
            >
              <Heading level={1} variant="headlineL">
                New Application
              </Heading>
              {application ? (
                <ApplicationStateTag
                  state={application.attributes?.applicationState}
                  hasOpenDocumentRequests={application.attributes?.hasOpenDocumentRequests}
                />
              ) : null}
            </Flex>
            {application ? (
              <DropdownMenu
                application={application}
                onEditProgram={() => {
                  isEditingProgramRef.current = true
                  setCurrentStep(ApplicationSteps.PROGRAM_SELECT)
                }}
                onEditAlternateProgram={() => {
                  isEditingProgramRef.current = true
                  setCurrentStep(ApplicationSteps.ALTERNATE_PROGRAM_SELECT)
                }}
              />
            ) : null}
          </Flex>
          <GridAlpha
            columnGap={12}
            rowGap={5}
            columns={{
              xs: 'minmax(0, 1fr)',
              md: '336px minmax(0, 1fr)',
            }}
            areas={{
              xs: ['aside', 'form'],
              md: ['aside form'],
            }}
          >
            <GridAlpha.Item areaName="aside">
              <Flex direction="column" gap={4}>
                <Flex
                  hideBelow={selectedTab > lastTimeline && showSubmitApprovalDialog ? 'sm' : undefined}
                  direction="column"
                >
                  <DesktopHeader
                    application={application || (temporaryApplication as StudentApplication)}
                  />
                </Flex>
                <ApplicationTimeline
                  selectedTab={selectedTab}
                  setSelectedTab={tab => {
                    setOpenIntakeClosedDialog(intake?.attributes?.isAvailable === false)
                    setCurrentStep(tab + 1)
                  }}
                  application={application || (temporaryApplication as StudentApplication)}
                />
              </Flex>
            </GridAlpha.Item>
            <GridAlpha.Item areaName="form">
              <Flex
                grow={1}
                hideBelow={showSubmitApprovalDialog || showSubmitConfirmation ? 'sm' : undefined}
              >
                <ApplicationForms
                  selectedTab={selectedTab}
                  application={application || (temporaryApplication as StudentApplication)}
                  focusRef={paySubmitButtonRef}
                  onPreviousTabButton={onPreviousTabButton}
                  onSuccess={response => {
                    if (response && selectedTab <= lastTimeline) {
                      queryClient.invalidateQueries({
                        queryKey: ['applications', response.data.id],
                      })
                      setCurrentStep(selectedTab + 2)
                    }
                  }}
                  onCreatedApplication={onCreatedApplication}
                  onError={(err: Error) => {
                    if (err.message.trim() !== '') {
                      if (err.message.includes('Application is in state PAYMENT_PROCESSING and thus is not editable.')) {
                        toast.negative(new Error('A payment for this application is already in progress. Please wait until it is completed before trying again. If the payment fails, you will be able to retry at that time.'))
                      } else {
                        toast.negative(err)
                      }
                    }
                    queryClient.invalidateQueries({ queryKey: ['applications', application?.id] })
                  }}
                  submitApplication={handleSubmitApplicationTrigger}
                  currentIntakeAvailable={intake?.attributes.isAvailable}
                  onIntakeUnavailable={() => {
                    setOpenIntakeClosedDialog(intake?.attributes?.isAvailable === false)
                  }}
                  isCreatingApplication={true}
                />
              </Flex>

              {hasApplicationFee(application) ? (
                <PayAndSubmitApprovalDialog
                  showSubmitApprovalDialog={showSubmitApprovalDialog}
                  setShowSubmitApprovalDialog={setShowSubmitApprovalDialog}
                  application={application as StudentApplication}
                />
              ) : (
                <SubmitApprovalDialog
                  showSubmitApprovalDialog={showSubmitApprovalDialog}
                  setShowSubmitApprovalDialog={setShowSubmitApprovalDialog}
                  onSubmitConfirmation={handleSubmitConfirmation}
                  isLoading={isSubmittingApplication}
                />
              )}

              <SubmitConfirmation
                showSubmitConfirmation={showSubmitConfirmation}
                setShowSubmitConfirmation={setShowSubmitConfirmation}
                onClose={() => {
                  navigate(`/applications/${application?.id}`)
                }}
              />
            </GridAlpha.Item>
          </GridAlpha>
        </Flex>
      </NavBars>
      <IntakeClosedDialog
        open={openIntakeClosedDialog}
        onOpenChange={setOpenIntakeClosedDialog}
        onEditProgram={() => {
          isEditingProgramRef.current = true
          setCurrentStep(ApplicationSteps.PROGRAM_SELECT)
        }}
      />

      <SpecificFieldsValidationErrorsDialog
        open={showSpecificFieldsValidationErrorsDialog}
        onOpenChange={setShowSpecificFieldsValidationErrorsDialog}
        errors={specificFieldsValidationErrors}
      />

      <PolicyBlockedDialog
        showPolicyBlockedDialog={showPolicyBlockedDialog}
        setShowPolicyBlockedDialog={setShowPolicyBlockedDialog}
        studentName={student?.attributes?.personalInformation?.previousName ?? student?.attributes?.personalInformation?.preferredName ?? undefined}
        intakeTerm={application?.attributes?.programSelected?.programIntakeTerm?.name}
        onEditProgram={() => {
          isEditingProgramRef.current = true
          setCurrentStep(ApplicationSteps.PROGRAM_SELECT)
        }}
      />
    </ApplicationWrapper>
  )
}

const ApplicationWrapper = styled.div({
  outline: 'none',
})
